/**
 * Build the same data shape that Chat.js's `parseBookingCard` returns, but sourced
 * from the structured `raw_output.booking` object on update_booking awaiting_confirmation
 * messages.
 *
 * Why this exists: the backend's `output` text for update_booking confirmation does
 * NOT include an `Image:` line, so `parseBookingCard` (which gates on `Image:`) returns
 * null. Rather than alter that path, we synthesize equivalent data from the structured
 * `booking` object so the existing booking-confirmation-card JSX can render unchanged.
 *
 * Returns null when:
 *  - message isn't an update_booking
 *  - conversation_state isn't `update_awaiting_confirmation`
 *  - `raw_output.booking` is missing
 *  - the booking has no resource_name AND no raw_output.room_name
 *
 * Image is optional — some rooms (e.g. Pink Conference Room) have no resource_image_url.
 * The card JSX in Chat.js renders the image area only when `image` is truthy.
 *
 * @param {object} message - chat message object
 * @returns {object|null} booking card data + headerText, matching parseBookingCard shape
 */
export function parseUpdateBookingConfirmation(message) {
  if (message?.function_name !== 'update_booking') return null;

  const raw = message?.raw_output;
  if (!raw || raw.conversation_state !== 'update_awaiting_confirmation') return null;

  const booking = raw.booking;
  if (!booking || typeof booking !== 'object') return null;

  const room = booking.resource_name || raw.room_name || '';
  if (!room) return null;

  const image = booking.resource_image_url || raw.room_image || null;

  const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${mins}m`;
  };

  const capacity = booking.resource_capacity
    ? `${booking.resource_capacity} ${booking.resource_capacity === 1 ? 'person' : 'people'}`
    : '';

  const rateParts = [];
  if (booking.price_display) rateParts.push(booking.price_display);
  if (booking.rate_name) rateParts.push(booking.rate_name);
  const rate = rateParts.join(', ');

  return {
    headerText: raw.update_header || '',
    room,
    location: booking.location_name || '',
    date: booking.date_converted || '',
    start: booking.start_time_converted || '',
    end: booking.end_time_converted || '',
    duration: formatDuration(booking.duration_minutes) || '',
    capacity,
    image,
    rate,
    reference: booking.reference || '',
    amenities: Array.isArray(booking.amenity_titles) ? booking.amenity_titles : [],
    actionText: raw.selection_prompt || '',
    isProposal: true,
    isConfirmed: false,
  };
}
